import { getPublicSuffix } from "tldts";
import type { OrderListItem } from "../../order/index.js";
import type { Project } from "../../project/index.js";
import { provideReact } from "../../react/index.js";
import type { DomainListItem } from "../Domain/index.js";
import type { IngressListItem } from "../Ingress/index.js";
import type { TldListItem } from "../Tld/index.js";
import { Tld } from "../Tld/index.js";
import type { IngressDomainListItem, ParseDomainOptions } from "./types.js";

export class IngressDomainList {
  public readonly items: IngressDomainListItem[];
  public constructor(
    ingresses: IngressListItem[],
    domains: DomainListItem[],
    orders: OrderListItem[],
    tlds: TldListItem[],
  ) {
    const ingressesWithDomains: IngressDomainListItem[] = [];

    ingresses.forEach((ingress: IngressListItem) => {
      const domain = domains.find(
        (domain) => domain.domain === ingress.hostname,
      );
      const type = domain
        ? "domain"
        : ingress.isSubdomain
          ? "subdomain"
          : "vhost";

      if (domain) {
        ingressesWithDomains.push({
          type: "domain",
          ingress,
          domain: domain,
          hostname: ingress.hostname,
          tld: tlds.find(
            (i) => i.tld === this.getTldOfDomain(ingress.hostname),
          ),
        });
        return;
      }

      ingressesWithDomains.push({
        ingress,
        domain: domain,
        type,
        hostname: ingress.hostname,
        tld: tlds.find((i) => i.tld === this.getTldOfDomain(ingress.hostname)),
      });
    });
    domains.forEach((domain: DomainListItem) => {
      const foundDomain = ingressesWithDomains.find(
        (d) => d.hostname === domain.domain,
      );
      if (foundDomain) {
        return;
      }
      ingressesWithDomains.push({
        domain: domain,
        type: "domain",
        hostname: domain.domain,
        tld: tlds.find((i) => i.tld === this.getTldOfDomain(domain.domain)),
      });
    });

    orders.forEach((order: OrderListItem) => {
      const domainAttribute = order.getOrderItemAttribute("domain");

      if (domainAttribute && domainAttribute.value) {
        const domain = domainAttribute.value;
        ingressesWithDomains.push({
          order: order,
          hostname: domain,
          tld: tlds.find((i) => i.tld === this.getTldOfDomain(domain)),
          type: "domain",
        });
      }
    });

    this.items = ingressesWithDomains;
  }

  private getTldOfDomain(
    domain: string,
    options?: ParseDomainOptions,
  ): string | undefined {
    const tld = getPublicSuffix(domain.trim(), options);
    if (tld !== null && tld !== "") {
      return tld;
    }
    return undefined;
  }

  public static ofProject = provideReact(async (project: Project) => {
    const [domainList, ingressList, tldList, orderList] = await Promise.all([
      project.domains.execute(),
      project.ingresses.execute(),
      Tld.query().execute(),
      project.orders
        .refine({ templateNames: ["domain"], includesStatus: ["CONFIRMED"] })
        .executeOptional(),
    ]);
    const ingresses = ingressList.items.map((i) => i);
    const domains = domainList.items.map((i) => i);
    const tlds = tldList.items.map((i) => i);
    const orders = orderList.items.filter((i) => i.type === "NEW_ORDER");
    return new IngressDomainList(ingresses, domains, orders, tlds);
  });
}
